import React, { useEffect, useState } from 'react';
import './EngagementTimeline.scss';
import GanttChartView from './GanttChartView/GanttChartView';
import { useSearchParams } from 'react-router-dom';
import { GanttChartItem } from '../../GanttChartComponent/models/GanttChartItem.model';
import {
    MarketplaceEngagementTimelineSubTab,
    MarketplaceEngagementTimelineSubTabs,
} from '../../../slices/marketplace/timeline/models/enums/seller-marketplace-engagement-timeline-sub-tab.enum';
import ProcessView from './ProcessView/ProcessView';
import { MarketplaceCalendar } from '../../../components/Marketplace/MarketplaceCalendar';
import InfoTooltip from '../../../components/info-tooltip/info-tooltip';
import ReactDOMServer from 'react-dom/server';
import { ChevronDiagramEvent } from '../../AdminMarketplace/ChevronProcessDiagram/models/ChevronDiagramEvent';
import { TimelineParty } from '../../../slices/marketplace/timeline/models/enums/timeline-party.enum';
import {
    TimelineEvent,
    UpdateEventStatus,
} from '../../../slices/marketplace/timeline/models/timeline-event.model';
import { EngagementData } from '../../../slices/marketplace/timeline/models/engagement-data.model';
import TimelineProcessViewLegend from '../../../components/TimelineProcessViewLegend/TimelineProcessViewLegend';
import TimelineEventsPartyLegend from '../../../components/TimelineEventsPartyLegend/TimelineEventsPartyLegend';
import { EngagementBuyerActionState } from '../../../slices/marketplace/timeline/models/engagement-buyer-action-state.model';
import { TimelineEventStatus } from '../../../slices/marketplace/timeline/models/enums/timeline-event-status.enum';
import { useCurrentMarketplaceParty } from '../../../custom-hooks/useCurrentMarketplaceParty';
import { Button } from 'reactstrap';
import ChangeTempoPopup from './ChangeTempoPopup/ChangeTempoPopup';
import { TimelineTempo } from '../../../slices/marketplace/timeline/models/enums/timeline-tempo.enum';
import { LevelOfService } from '../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import PreviewTempoSelector from './PreviewTempoSelector/PreviewTempoSelector';
import { TimelineEventSortingService } from '../../AdminMarketplace/TimelinePage/TimelineEventSortingService';
import { isEventActionClickable } from '../EventActionAvailabilityHelper';
import { NotificationFlag } from '../../../components/NotificationFlag/NotificationFlag';
import { NotificationFlagsCategories } from '../../../models/NotificationFlags/NotificationFlagsCategories';

interface MyEngagementProps {
    id: number;
    events: TimelineEvent[];
    buyerActionState?: EngagementBuyerActionState[];
    engagementData: EngagementData;
    startDate: Date;
    setSearchParams: (params: { [key: string]: string }) => void;
    onEventClick(id: string): void;
    isReadOnly: boolean;
    tempo?: TimelineTempo;
    levelOfService?: LevelOfService;
    fetchData?: () => void;
    recalculateEventsBasedOnTempo?: (newTempo: TimelineTempo) => void;
    isBuyerView: boolean;
}

const EngagementTimeline = (props: MyEngagementProps) => {
    const [activeTab, setActiveTab] = useState<number>(
        MarketplaceEngagementTimelineSubTab.ProcessView
    );
    const [searchParams] = useSearchParams({});

    const party = useCurrentMarketplaceParty(props.id);

    const [isChangeTempoPopupVisible, setIsChangeTempoPopupVisible] =
        useState<boolean>(false);

    const [isPreviewTempo, setIsPreviewTempo] = useState<boolean>(false);

    const checkEventOrder = (tempo: TimelineTempo) => {
        TimelineEventSortingService.sortTimelineEvents(
            props.events,
            props.levelOfService,
            tempo
        );
    };

    const getEvents = () => {
        const isSuspendEventExist = props.events.some(
            (x) => x.isSuspendEvent && x.dayCompleted === null
        );
        if (isSuspendEventExist) {
            return props.events.map((x) =>
                x.isSuspendEvent
                    ? x
                    : UpdateEventStatus(x, TimelineEventStatus.Upcoming)
            );
        }
        return props.events;
    };

    const tabComponents = [
        {
            id: MarketplaceEngagementTimelineSubTab.ProcessView,
            content: () => (
                <ProcessView
                    timelineEvents={getEvents()}
                    dataRequestProjects={
                        props.engagementData?.dataRequestProjects
                    }
                    engagementStartDate={props.startDate}
                    onActionClick={(item: ChevronDiagramEvent) => {
                        props.onEventClick(item.id);
                    }}
                    isEventActionVisible={(eventId) =>
                        isEventActionClickable(
                            eventId,
                            props.isReadOnly,
                            props.engagementData,
                            props.events,
                            party,
                            props.buyerActionState
                        )
                    }
                    isTempoPreview={isPreviewTempo}
                    engagementId={props.id}
                    isBuyerView={props.isBuyerView}
                />
            ),
        },
        {
            id: MarketplaceEngagementTimelineSubTab.GanttChartView,
            content: () => (
                <GanttChartView
                    timelineEvents={getEvents()}
                    buyerActionState={props.buyerActionState}
                    startDate={new Date(props.startDate)}
                    onEventClick={(item: GanttChartItem) => {
                        props.onEventClick(item.id);
                    }}
                    isEventClickable={(eventId) =>
                        isEventActionClickable(
                            eventId,
                            props.isReadOnly,
                            props.engagementData,
                            props.events,
                            party,
                            props.buyerActionState
                        )
                    }
                />
            ),
        },
        {
            id: MarketplaceEngagementTimelineSubTab.CalendarView,
            content: () => (
                <MarketplaceCalendar
                    timelineEvents={getEvents()}
                    startDate={new Date(props.startDate)}
                    timelineId={props.id}
                    onEventClick={props.onEventClick}
                    canUserRedirectToAction={(eventId) =>
                        isEventActionClickable(
                            eventId,
                            props.isReadOnly,
                            props.engagementData,
                            props.events,
                            party,
                            props.buyerActionState
                        )
                    }
                />
            ),
        },
    ];

    useEffect(() => {
        const value = searchParams.get('subtab');
        const numberValue = Number(value);
        if (
            value &&
            numberValue &&
            numberValue >= 1 &&
            numberValue <= tabComponents.length
        ) {
            setActiveTab(numberValue);
        }
        if (numberValue > tabComponents.length) {
            props.setSearchParams({
                subTab: Number(
                    MarketplaceEngagementTimelineSubTab.ProcessView
                ).toString(),
            });
        }
    }, [searchParams]);

    const renderTabContent = () => {
        const activeTabData = tabComponents.find((tab) => tab.id === activeTab);
        return activeTabData ? activeTabData.content() : <></>;
    };

    const getTabTooltip = (subTabId: MarketplaceEngagementTimelineSubTab) => {
        switch (subTabId) {
            case MarketplaceEngagementTimelineSubTab.CalendarView:
                return (
                    <InfoTooltip
                        idText="calendar-view-info-tooltip"
                        text=""
                        place="bottom"
                        html={ReactDOMServer.renderToString(
                            <TimelineEventsPartyLegend />
                        )}
                    />
                );
            case MarketplaceEngagementTimelineSubTab.GanttChartView:
                return (
                    <InfoTooltip
                        idText="gantt-chart-info-tooltip"
                        text=""
                        place="bottom"
                        html={ReactDOMServer.renderToString(
                            <TimelineEventsPartyLegend />
                        )}
                    />
                );
            case MarketplaceEngagementTimelineSubTab.ProcessView:
                return (
                    <InfoTooltip
                        idText="process-view-tooltip"
                        text=""
                        place="bottom"
                        html={ReactDOMServer.renderToString(
                            <TimelineProcessViewLegend />
                        )}
                    />
                );
            default:
                return <></>;
        }
    };

    const renderTabs = () => {
        return (
            <div
                key={props.events.length}
                className="body_inner_content gantt-chart-container">
                <div className="RRT__container">
                    <div className="RRT__tabs body-tabs">
                        {MarketplaceEngagementTimelineSubTabs?.map((item) => {
                            return (
                                <div
                                    key={item.id}
                                    className={
                                        activeTab === item.id
                                            ? 'RRT__tab RRT__tab--first RRT__tab--selected'
                                            : 'RRT__tab'
                                    }
                                    onClick={() => {
                                        setActiveTab(item.id);
                                        props.setSearchParams({
                                            subtab: item.id.toString(),
                                        });
                                    }}>
                                    {item.content}
                                    {getTabTooltip(item.id)}
                                    {item.id ===
                                    MarketplaceEngagementTimelineSubTab.ProcessView ? (
                                        <NotificationFlag
                                            flagClassName="notification-flag-wrapper"
                                            eventCode={`${
                                                NotificationFlagsCategories.MpCode
                                            }.${
                                                props.isBuyerView
                                                    ? `${NotificationFlagsCategories.OpportunitiesCode}.`
                                                    : ''
                                            }${props.id}.${
                                                NotificationFlagsCategories.EngagementTimelinePreviewCode
                                            }`}
                                            includePrefix={true}
                                        />
                                    ) : (
                                        <></>
                                    )}
                                </div>
                            );
                        })}
                        {party !== TimelineParty.Buyer ? (
                            <div className="tempo-container">
                                <PreviewTempoSelector
                                    currentTempo={props.tempo}
                                    onTempoChange={
                                        props.recalculateEventsBasedOnTempo
                                    }
                                    setIsPreviewTempo={setIsPreviewTempo}
                                />

                                {party === TimelineParty.Seller ? (
                                    <Button
                                        color="primary"
                                        className="select-documents-btn"
                                        onClick={() => {
                                            setIsChangeTempoPopupVisible(true);
                                        }}>
                                        Change Tempo
                                    </Button>
                                ) : (
                                    []
                                )}
                            </div>
                        ) : (
                            []
                        )}
                    </div>
                </div>
                {renderTabContent()}
            </div>
        );
    };

    return (
        <>
            <div className="engagement-timeline gantt-chart-container">
                {renderTabs()}
            </div>
            {isChangeTempoPopupVisible ? (
                <ChangeTempoPopup
                    isVisible={isChangeTempoPopupVisible}
                    setIsVisible={setIsChangeTempoPopupVisible}
                    fetchData={props.fetchData}
                    checkEventOrder={checkEventOrder}
                />
            ) : (
                <></>
            )}
        </>
    );
};

export default EngagementTimeline;
